// Feature flag: when CONTENT_LIBRARY_PROVIDER_MODE is enabled, redirect to
// the ProviderShell iframe which renders the standalone Content Library app.
const PROVIDER_MODE = process.env.REACT_APP_CONTENT_LIBRARY_PROVIDER_MODE === 'true';

// ─── Legacy ContentLibrary (direct MongoDB via runtime API) ──────────────────

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FolderOpen, Upload, Search, Image, FileText, Video, ChevronRight, ChevronDown,
    Trash2, Edit3, Sparkles, Star, ExternalLink, Download, LayoutGrid, List,
    Plus, X, Link2, Loader2, MoreVertical, Move, Eye, Globe, FolderPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { presignedUpload, uploadErrorText } from '../lib/presignedUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatFileSize(bytes) {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getVideoEmbedUrl(url, provider) {
    if (!url) return null;
    if (provider === 'youtube') {
        const match = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    }
    if (provider === 'loom') {
        const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
        return match ? `https://www.loom.com/embed/${match[1]}` : null;
    }
    if (provider === 'vimeo') {
        const match = url.match(/vimeo\.com\/(\d+)/);
        return match ? `https://player.vimeo.com/video/${match[1]}` : null;
    }
    return null;
}

function getFileIcon(type) {
    switch (type) {
        case 'image': return Image;
        case 'video': return Video;
        default: return FileText;
    }
}

function getTypeColor(type) {
    switch (type) {
        case 'image': return { bg: 'bg-amber-50', text: 'text-amber-500', badge: 'bg-amber-100 text-amber-700' };
        case 'video': return { bg: 'bg-violet-50', text: 'text-violet-500', badge: 'bg-violet-100 text-violet-700' };
        default: return { bg: 'bg-stone-100', text: 'text-stone-500', badge: 'bg-stone-100 text-stone-600' };
    }
}

// ─── Folder Tree ─────────────────────────────────────────────────────────────

function FolderTree({ folders, selectedFolder, onSelect }) {
    const [expanded, setExpanded] = useState({});

    const toggleExpand = (name) => {
        setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
    };

    return (
        <div className="space-y-0.5">
            <button
                onClick={() => onSelect('all')}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${selectedFolder === 'all' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
            >
                <FolderOpen className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">All Assets</span>
            </button>
            {folders.map(folder => (
                <div key={folder.name}>
                    <button
                        onClick={() => {
                            toggleExpand(folder.name);
                            onSelect(folder.name);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${selectedFolder === folder.name ? 'bg-stone-200 text-stone-900 font-medium' : 'text-stone-600 hover:bg-stone-100'}`}
                    >
                        {folder.children?.length > 0 ? (
                            expanded[folder.name] ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                        ) : <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span className="truncate flex-1 text-left">{folder.name}</span>
                        {folder.count > 0 && <span className="text-xs text-stone-400">{folder.count}</span>}
                    </button>
                    {expanded[folder.name] && folder.children?.length > 0 && (
                        <div className="ml-4 space-y-0.5">
                            {folder.children.map(child => (
                                <button
                                    key={child.path}
                                    onClick={() => onSelect(child.path)}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg transition-colors ${selectedFolder === child.path ? 'bg-stone-200 text-stone-900 font-medium' : 'text-stone-500 hover:bg-stone-50'}`}
                                >
                                    <span className="truncate flex-1 text-left">{child.name}</span>
                                    {child.count > 0 && <span className="text-xs text-stone-400">{child.count}</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ─── Asset Preview Modal ─────────────────────────────────────────────────────

function AssetPreviewModal({ asset, open, onOpenChange, onUpdate, onAnalyze, getAuthHeader }) {
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editTags, setEditTags] = useState('');
    const [analyzing, setAnalyzing] = useState(false);

    useEffect(() => {
        if (asset) {
            setEditName(asset.name || '');
            setEditDesc(asset.description || '');
            setEditTags((asset.tags || []).join(', '));
            setEditing(false);
        }
    }, [asset]);

    const handleSave = async () => {
        try {
            await axios.put(`${API}/content-library/${asset.asset_id}`, {
                name: editName,
                description: editDesc,
                tags: editTags.split(',').map(t => t.trim()).filter(Boolean),
            }, { headers: getAuthHeader() });
            toast.success('Asset updated');
            onUpdate();
            setEditing(false);
        } catch { toast.error('Update failed'); }
    };

    const handleAnalyze = async () => {
        setAnalyzing(true);
        try {
            await axios.post(`${API}/content-library/${asset.asset_id}/ai-analyze`, {}, { headers: getAuthHeader() });
            toast.success('AI metadata generated');
            onAnalyze();
        } catch { toast.error('AI analysis failed'); }
        finally { setAnalyzing(false); }
    };

    if (!asset) return null;

    const Icon = getFileIcon(asset.file_type);
    const colors = getTypeColor(asset.file_type);
    const embedUrl = getVideoEmbedUrl(asset.external_url, asset.video_provider);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${colors.text}`} />
                        {editing ? (
                            <Input value={editName} onChange={e => setEditName(e.target.value)} className="flex-1" />
                        ) : asset.name}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Preview */}
                    {asset.file_type === 'image' && asset.url && (
                        <div className="bg-stone-50 rounded-lg p-4 flex items-center justify-center">
                            <img src={asset.url} alt={asset.name} className="max-h-64 rounded-lg object-contain" />
                        </div>
                    )}
                    {asset.file_type === 'video' && embedUrl && (
                        <div className="bg-black rounded-lg overflow-hidden aspect-video">
                            <iframe src={embedUrl} title={asset.name} className="w-full h-full" allowFullScreen frameBorder="0" />
                        </div>
                    )}
                    {asset.file_type === 'document' && (
                        <div className={`${colors.bg} rounded-lg p-8 flex flex-col items-center gap-2`}>
                            <Icon className={`w-16 h-16 ${colors.text}`} />
                            <span className="text-sm text-stone-500">{asset.file_name || asset.name}</span>
                        </div>
                    )}

                    {/* Metadata */}
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs text-stone-500">Description</Label>
                            {editing ? (
                                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={3}
                                    className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            ) : (
                                <p className="text-sm text-stone-700 mt-1">{asset.description || 'No description'}</p>
                            )}
                        </div>

                        <div>
                            <Label className="text-xs text-stone-500">Tags</Label>
                            {editing ? (
                                <Input value={editTags} onChange={e => setEditTags(e.target.value)} placeholder="tag1, tag2, tag3" className="mt-1" />
                            ) : (
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {(asset.tags || []).length > 0 ? asset.tags.map(t => (
                                        <span key={t} className="px-2 py-0.5 text-xs bg-stone-100 text-stone-600 rounded-full">{t}</span>
                                    )) : <span className="text-xs text-stone-400">No tags</span>}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-xs text-stone-500">
                            <div><span className="font-medium">Type:</span> {asset.file_type}</div>
                            <div><span className="font-medium">Size:</span> {formatFileSize(asset.file_size)}</div>
                            <div><span className="font-medium">Folder:</span> {asset.folder_path}</div>
                            <div><span className="font-medium">Uploaded by:</span> {asset.uploaded_by_name}</div>
                        </div>

                        {/* AI Metadata */}
                        {asset.ai_metadata && (
                            <div className="bg-violet-50 rounded-lg p-3">
                                <div className="text-xs font-semibold text-violet-700 mb-2 flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" /> AI Analysis
                                </div>
                                {asset.ai_metadata.content_type && (
                                    <p className="text-xs text-violet-600 mb-1">Content type: {asset.ai_metadata.content_type}</p>
                                )}
                                {asset.ai_metadata.suggested_use_cases?.length > 0 && (
                                    <p className="text-xs text-violet-600">Use cases: {asset.ai_metadata.suggested_use_cases.join(', ')}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={handleAnalyze} disabled={analyzing}>
                                {analyzing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                                AI Analyze
                            </Button>
                            {asset.url && (
                                <a href={asset.download_url || asset.url} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
                                </a>
                            )}
                            {asset.external_url && (
                                <a href={asset.external_url} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open</Button>
                                </a>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {editing ? (
                                <>
                                    <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                                    <Button size="sm" onClick={handleSave}>Save</Button>
                                </>
                            ) : (
                                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Main Content Library Page ───────────────────────────────────────────────

// Wrapper: when provider mode is enabled, redirect to the ProviderShell iframe
// which renders the standalone Content Library app. Otherwise, render the
// legacy implementation.
export default function ContentLibrary() {
    if (PROVIDER_MODE) {
        return <Navigate to="/modules/prov_content_library" replace />;
    }
    return <ContentLibraryLegacy />;
}

function ContentLibraryLegacy() {
    const { getAuthHeader, user } = useAuth();
    const [assets, setAssets] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [viewMode, setViewMode] = useState('grid');
    const [showUpload, setShowUpload] = useState(false);
    const [showVideoUrl, setShowVideoUrl] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [videoName, setVideoName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [previewAsset, setPreviewAsset] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const loadData = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (selectedFolder !== 'all') params.set('folder', selectedFolder);
            if (filterType !== 'all') params.set('file_type', filterType);
            if (search) params.set('search', search);
            params.set('sort', sortBy);

            const [assetsRes, foldersRes] = await Promise.all([
                axios.get(`${API}/content-library?${params}`, { headers: getAuthHeader() }),
                axios.get(`${API}/content-library/folders`, { headers: getAuthHeader() }),
            ]);
            setAssets(assetsRes.data);
            setFolders(foldersRes.data);
        } catch { toast.error('Failed to load content library'); }
        finally { setLoading(false); }
    }, [selectedFolder, filterType, search, sortBy, getAuthHeader]);

    useEffect(() => { loadData(); }, [loadData]);

    // ─── Upload Handlers ─────────────────────────────────────────────────

    const uploadFile = async (file) => {
        setUploading(true);
        try {
            // Step 1: Get a presigned POST
            const { data: { upload, s3_key } } = await axios.post(`${API}/content-library/upload-url`, {
                file_name: file.name,
                mime_type: file.type || 'application/octet-stream',
                size: file.size,
            }, { headers: getAuthHeader() });

            // Step 2: Upload to S3. Fails closed (CASA 5.2.1): a response without
            // a POST policy — e.g. a provider still handing out a presigned PUT —
            // is refused, never uploaded to. The runtime proxy answers 410 today.
            await presignedUpload(upload, file);

            // Step 3: Register asset
            const folderPath = selectedFolder !== 'all' ? selectedFolder : 'General';
            const { data: asset } = await axios.post(`${API}/content-library`, {
                name: file.name.replace(/\.[^.]+$/, ''),
                file_type: file.type?.startsWith('image/') ? 'image' : file.type?.startsWith('video/') ? 'video' : 'document',
                mime_type: file.type,
                file_name: file.name,
                file_size: file.size,
                s3_key: s3_key,
                folder_path: folderPath,
            }, { headers: getAuthHeader() });

            toast.success(`"${file.name}" uploaded`);

            // Step 4: Trigger AI analysis in background
            axios.post(`${API}/content-library/${asset.asset_id}/ai-analyze`, {}, { headers: getAuthHeader() })
                .then(() => { toast.success(`AI metadata generated for "${file.name}"`); loadData(); })
                .catch(() => {});

            loadData();
        } catch (err) {
            toast.error(`Upload failed: ${uploadErrorText(err, 'please try again')}`);
        } finally {
            setUploading(false);
        }
    };

    const handleFiles = (files) => {
        Array.from(files).forEach(file => uploadFile(file));
        setShowUpload(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    };

    const handleVideoSubmit = async () => {
        if (!videoUrl.trim()) return;
        setUploading(true);
        try {
            const folderPath = selectedFolder !== 'all' ? selectedFolder : 'General';
            const { data: asset } = await axios.post(`${API}/content-library`, {
                name: videoName || videoUrl,
                file_type: 'video',
                external_url: videoUrl,
                folder_path: folderPath,
            }, { headers: getAuthHeader() });

            toast.success('Video added');
            setShowVideoUrl(false);
            setVideoUrl('');
            setVideoName('');

            // AI analyze
            axios.post(`${API}/content-library/${asset.asset_id}/ai-analyze`, {}, { headers: getAuthHeader() })
                .then(() => { toast.success('AI metadata generated'); loadData(); })
                .catch(() => {});

            loadData();
        } catch { toast.error('Failed to add video'); }
        finally { setUploading(false); }
    };

    const handleDelete = async (assetId) => {
        if (!window.confirm('Delete this asset?')) return;
        try {
            await axios.delete(`${API}/content-library/${assetId}`, { headers: getAuthHeader() });
            toast.success('Asset deleted');
            loadData();
        } catch { toast.error('Delete failed'); }
    };

    const openPreview = async (asset) => {
        try {
            const { data } = await axios.get(`${API}/content-library/${asset.asset_id}`, { headers: getAuthHeader() });
            setPreviewAsset(data);
            setShowPreview(true);
        } catch { toast.error('Failed to load asset'); }
    };

    // ─── Render ──────────────────────────────────────────────────────────

    if (loading) return (
        <div className="flex items-center justify-center h-96">
            <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
        </div>
    );

    return (
        <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
            {/* Sidebar — Folder Tree */}
            <div className="w-64 border-r border-stone-200 p-4 overflow-y-auto flex-shrink-0" style={{ backgroundColor: '#FAFAF5' }}>
                <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Folders</h3>
                <FolderTree folders={folders} selectedFolder={selectedFolder} onSelect={setSelectedFolder} />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden"
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
            >
                {/* Header Bar */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
                    <div>
                        <h1 className="text-lg font-semibold text-stone-900">Content Library</h1>
                        <p className="text-xs text-stone-500">{assets.length} asset{assets.length !== 1 ? 's' : ''}{selectedFolder !== 'all' ? ` in ${selectedFolder}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowVideoUrl(true)}>
                            <Link2 className="w-4 h-4 mr-1" /> Add Video URL
                        </Button>
                        <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-stone-900 hover:bg-stone-800">
                            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                            Upload
                        </Button>
                        <input ref={fileInputRef} type="file" multiple className="hidden"
                            onChange={e => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }} />
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 px-6 py-3 border-b border-stone-100">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assets..." className="pl-10 h-9" />
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                        {['all', 'image', 'document', 'video'].map(t => (
                            <button key={t} onClick={() => setFilterType(t)}
                                className={`px-3 py-1.5 rounded-full transition-colors ${filterType === t ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                                {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1) + 's'}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                            className="text-xs px-2 py-1.5 border border-stone-200 rounded-lg bg-white">
                            <option value="name">Name A-Z</option>
                            <option value="created_at">Newest</option>
                            <option value="file_size">Size</option>
                        </select>
                        <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-stone-200' : 'hover:bg-stone-100'}`}>
                            <LayoutGrid className="w-4 h-4 text-stone-600" />
                        </button>
                        <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-stone-200' : 'hover:bg-stone-100'}`}>
                            <List className="w-4 h-4 text-stone-600" />
                        </button>
                    </div>
                </div>

                {/* Drag Overlay */}
                {dragActive && (
                    <div className="absolute inset-0 bg-stone-900/10 backdrop-blur-sm z-50 flex items-center justify-center pointer-events-none">
                        <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-dashed border-stone-400">
                            <Upload className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                            <p className="text-lg font-semibold text-stone-700">Drop files to upload</p>
                        </div>
                    </div>
                )}

                {/* Asset Grid / List */}
                <div className="flex-1 overflow-y-auto p-6">
                    {assets.length === 0 ? (
                        <div className="text-center py-16 border-2 border-dashed border-stone-200 rounded-xl">
                            <FolderOpen className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                            <h3 className="text-lg font-semibold text-stone-700 mb-2">No assets yet</h3>
                            <p className="text-stone-500 mb-4 text-sm">Drag & drop files here, or click Upload to add content</p>
                            <Button onClick={() => fileInputRef.current?.click()}>
                                <Upload className="w-4 h-4 mr-2" /> Upload Your First Asset
                            </Button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {assets.map(asset => {
                                const Icon = getFileIcon(asset.file_type);
                                const colors = getTypeColor(asset.file_type);
                                return (
                                    <div key={asset.asset_id}
                                        className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-md transition-all cursor-pointer group"
                                        onClick={() => openPreview(asset)}
                                    >
                                        <div className={`h-28 flex items-center justify-center relative ${colors.bg}`}>
                                            {asset.file_type === 'image' && asset.url ? (
                                                <img src={asset.url} alt={asset.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <Icon className={`w-10 h-10 ${colors.text}`} />
                                            )}
                                            {asset.external_url && (
                                                <span className="absolute top-2 right-2 p-1 bg-white/80 rounded-md">
                                                    <Globe className="w-3 h-3 text-blue-500" />
                                                </span>
                                            )}
                                            {asset.ai_metadata && (
                                                <span className="absolute top-2 left-2 p-1 bg-violet-100/80 rounded-md">
                                                    <Sparkles className="w-3 h-3 text-violet-500" />
                                                </span>
                                            )}
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                                        </div>
                                        <div className="p-3">
                                            <p className="text-sm font-medium text-stone-800 truncate">{asset.name}</p>
                                            <div className="flex items-center justify-between mt-1">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${colors.badge}`}>{asset.file_type}</span>
                                                <span className="text-[10px] text-stone-400">{formatFileSize(asset.file_size)}</span>
                                            </div>
                                            {asset.tags?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    {asset.tags.slice(0, 3).map(t => (
                                                        <span key={t} className="text-[10px] px-1.5 py-0.5 bg-stone-50 text-stone-500 rounded">{t}</span>
                                                    ))}
                                                    {asset.tags.length > 3 && <span className="text-[10px] text-stone-400">+{asset.tags.length - 3}</span>}
                                                </div>
                                            )}
                                        </div>
                                        <div className="px-3 pb-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={e => e.stopPropagation()}>
                                            <button onClick={() => handleDelete(asset.asset_id)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {assets.map(asset => {
                                const Icon = getFileIcon(asset.file_type);
                                const colors = getTypeColor(asset.file_type);
                                return (
                                    <div key={asset.asset_id}
                                        className="flex items-center gap-4 px-4 py-3 bg-white border border-stone-200 rounded-lg hover:shadow-sm transition-all cursor-pointer group"
                                        onClick={() => openPreview(asset)}
                                    >
                                        <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                                            <Icon className={`w-5 h-5 ${colors.text}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-stone-800 truncate">{asset.name}</p>
                                            <p className="text-xs text-stone-400 truncate">{asset.description || asset.folder_path}</p>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-stone-400 flex-shrink-0">
                                            <span className={`px-2 py-0.5 rounded-full ${colors.badge}`}>{asset.file_type}</span>
                                            <span>{formatFileSize(asset.file_size)}</span>
                                            <span>{asset.folder_path?.split('/').pop()}</span>
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={e => e.stopPropagation()}>
                                            <button onClick={() => handleDelete(asset.asset_id)} className="p-1.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Asset Preview Modal */}
            <AssetPreviewModal
                asset={previewAsset}
                open={showPreview}
                onOpenChange={setShowPreview}
                onUpdate={loadData}
                onAnalyze={loadData}
                getAuthHeader={getAuthHeader}
            />

            {/* Video URL Modal */}
            <Dialog open={showVideoUrl} onOpenChange={setShowVideoUrl}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Video URL</DialogTitle>
                        <DialogDescription>Paste a YouTube, Loom, or Vimeo link</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Video URL *</Label>
                            <Input value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=... or https://www.loom.com/share/..." className="mt-1" />
                        </div>
                        <div>
                            <Label>Name</Label>
                            <Input value={videoName} onChange={e => setVideoName(e.target.value)}
                                placeholder="Product Demo Video" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowVideoUrl(false)}>Cancel</Button>
                        <Button onClick={handleVideoSubmit} disabled={uploading}>
                            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                            Add Video
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
